import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaTicketAlt, FaCalendarAlt, FaPlus, FaUser, FaCog, FaQrcode } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../firebase.marketplace';
import { Helmet } from 'react-helmet';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #030617 0%, #0a1128 100%);
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const Logo = styled(Link)`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  span {
    background: linear-gradient(90deg, #4b7bec, #a55eea);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    
    span {
      display: none;
    }
  }
`;

const AuthButton = styled.button`
  background: linear-gradient(90deg, #4b7bec, #3867d6);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    
    span {
      display: none;
    }
  }
`;

const Main = styled.main`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const Footer = styled.footer`
  background: rgba(0, 0, 0, 0.5);
  padding: 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
`;

const StreamingLayout = ({ children }) => {
  const { currentUser, hasRole, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogin = () => {
    navigate('/marketplace/auth/signin', { state: { returnUrl: '/streaming-events' } });
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/streaming-events');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };
  
  return (
    <Container>
      <Helmet>
        <title>Konzo Vibez - Live Events</title>
        <meta name="description" content="Stream live music events and performances from your favorite artists." />
      </Helmet>
      
      <Header>
        <Logo to="/streaming-events">
          <FaCalendarAlt />
          <span>KONZO</span> E-events
        </Logo>
        
        <Nav>
          <NavLink to="/streaming-events">
            <FaCalendarAlt />
            <span>Events</span>
          </NavLink>
          
          {currentUser && (
            <NavLink to="/streaming-events/my-tickets">
              <FaTicketAlt />
              <span>My Tickets</span>
            </NavLink>
          )}
          
          {currentUser && hasRole(ROLES.ARTIST) && (
            <>
              <NavLink to="/streaming-events/create-event">
                <FaPlus />
                <span>Create Event</span>
              </NavLink>
              <NavLink to="/streaming-events/manage-events">
                <FaCog />
                <span>Manage Events</span>
              </NavLink>
              <NavLink to="/streaming-events/verify-tickets">
                <FaQrcode />
                <span>Verify Tickets</span>
              </NavLink>
            </>
          )}
          
          {currentUser ? (
            <AuthButton onClick={handleLogout}>
              <FaUser />
              <span>Logout</span>
            </AuthButton>
          ) : (
            <AuthButton onClick={handleLogin}>
              <FaUser />
              <span>Login</span>
            </AuthButton>
          )}
        </Nav>
      </Header>
      
      <Main>
        {children}
      </Main>
      
      <Footer>
        &copy; {new Date().getFullYear()} Konzo E-events. All rights reserved.
      </Footer>
    </Container>
  );
};

export default StreamingLayout;
